import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, CheckCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useNoIndex } from "@/lib/useNoIndex";  // PR #97 / R8 — reset token in URL must not be indexed

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_VERTICAL = "/logos/solara-vertical.png";

export default function ResetPasswordPage() {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9] p-8">
        <div className="bg-white p-8 rounded shadow-sm border border-gray-200 max-w-md text-center">
          <h1 className="text-2xl font-bold text-[#021d49] mb-4">Invalid Link</h1>
          <p className="text-gray-500 mb-6">This password reset link is invalid or has expired.</p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 text-[#bd9a59] hover:underline font-medium"
          >
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#021d49] items-center justify-center p-12">
        <div className="text-center">
          <img src={LOGO_VERTICAL} alt="Solara Global" className="h-64 mx-auto mb-8" />
          <p className="text-white/70 text-lg max-w-md">
            Set your new password securely.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#fafaf9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img src={LOGO_VERTICAL} alt="Solara Global" className="h-24 mx-auto" />
          </div>

          <div className="bg-white p-8 rounded shadow-sm border border-gray-200">
            {success ? (
              <div className="text-center py-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="font-heading text-2xl font-bold text-[#021d49] mb-2">Password Reset!</h1>
                <p className="text-gray-500 mb-6">
                  Your password has been updated successfully. You can now log in with your new password.
                </p>
                <button
                  onClick={() => navigate("/login")}
                  className="w-full bg-[#021d49] hover:bg-[#052b66] text-white py-3 rounded font-medium transition-colors"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <>
                <h1 className="font-heading text-3xl font-bold text-[#021d49] mb-2">
                  Set New Password
                </h1>
                <p className="text-gray-500 mb-8">
                  Enter your new password below.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label className="form-label">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="form-input pr-10"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Confirm Password</Label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      className="form-input"
                      data-testid="reset-confirm-password-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#021d49] hover:bg-[#052b66] text-white py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="reset-submit-btn"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Lock className="w-5 h-5" />
                        Reset Password
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-gray-400 text-sm hover:text-[#bd9a59]"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
